import autobind from 'autobind-decorator';
import * as React from 'react';
import { connect } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';
import { Icons } from '@shared/ui-components';

import { RequestStatus } from 'common/enums/request-status';
import { State } from 'common/interfaces/state';
import { AccountActions } from '../../../../units/account/actions/creators';
import { ValidationField } from '../../../../units/account/actions/payloads';
import { PaymentMethodActions } from '../../actions/creators';
import { BillingCardModel } from '../../interfaces/billing-card-model';
import { BillingCountryModel } from '../../interfaces/billing-country-model';
import { PaymentMethodSelector } from '../payment-method-selector';
import { BillingAddress } from './billing-address';
import { Styled } from './styled';

interface StateProps {
  paymentMethods: BillingCardModel[];
  paymentMethodRequestStatus: RequestStatus;
  availableCountries: BillingCountryModel[];
  vatNumberValidation: string;
}

interface DispatchProps {
  getPaymentMethods: () => void;
  dropPaymentState: () => void;
  resetVatNumberValidation: () => void;
}

interface Props extends StateProps, DispatchProps {
  countryId: string;
  vatNumber: string;
  subscriptionCardId: string;
  setSubscriptionCard: (id: string) => void;
  setCountryId: (countryId: string) => void;
  setVatNumber: (vatNumber: string) => void;
}

class PaymentMethodManagerComponent extends React.Component<Props> {
  public componentDidMount(): void {
    this.props.getPaymentMethods();
  }

  public render(): JSX.Element {
    if (this.props.paymentMethodRequestStatus === RequestStatus.Loading) {
      return (
        <Styled.Container>
          <Styled.Spinner>
            <Icons.Loader />
          </Styled.Spinner>
        </Styled.Container>
      );
    }

    const paymentMethods = this.props.paymentMethods;
    const cards = paymentMethods && paymentMethods.map(x => ({ ...x, type: x.brand.toLowerCase() }));

    return (
      <Styled.Container existCards={cards && !!cards.length}>
        <BillingAddress
          availableCountries={this.props.availableCountries}
          selectedCountryId={this.props.countryId}
          vatNumber={this.props.vatNumber}
          onCountryChange={this.props.setCountryId}
          onVatNumberChange={this.onVatNumberChange}
          vatNumberErrorText={this.props.vatNumberValidation}
        />
        <PaymentMethodSelector
          subscriptionCardId={this.props.subscriptionCardId}
          setSubscriptionCard={this.props.setSubscriptionCard}
        />
      </Styled.Container>
    );
  }

  public componentWillUnmount(): void {
    this.props.dropPaymentState();
  }

  @autobind
  private onVatNumberChange(vatNumber: string): void {
    this.props.setVatNumber(vatNumber);
    this.props.resetVatNumberValidation();
  }
}

function mapStateToProps(state: State): StateProps {
  const payment = state.account.payment;

  return {
    paymentMethods: payment && payment.cards,
    paymentMethodRequestStatus: state.account.paymentStatus,
    availableCountries: state.account.billingCountries,
    vatNumberValidation: state.account.subscriptionValidation.vatNumberValidation,
  };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>): DispatchProps {
  return {
    getPaymentMethods: () => dispatch(PaymentMethodActions.getPaymentMethods()),
    dropPaymentState: () => dispatch(PaymentMethodActions.dropPaymentState()),
    resetVatNumberValidation: () => dispatch(AccountActions.setSubscribeError(ValidationField.VatNumber, '')),
  };
}

export const PaymentMethodManager = connect(mapStateToProps, mapDispatchToProps)(PaymentMethodManagerComponent);
